// src/firebase.js
import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
// import { getFirestore, FieldValue } from 'firebase/firestore';
import { 
    getFirestore, 
    FieldValue,
    serverTimestamp, // Explicitly importing serverTimestamp
    collection, 
    doc, 
    query, 
    where, 
    onSnapshot,
    getDoc,
    addDoc,
    updateDoc,
    orderBy
} from 'firebase/firestore'; 
// --- 1. Import getStorage ---
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { connectAuthEmulator } from 'firebase/auth';
import { connectFirestoreEmulator } from 'firebase/firestore';
import { connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';

// Production config is the default. A staging build overrides it with
// REACT_APP_FIREBASE_* variables (see staging/README.md).
const env = process.env;
const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyCM_YaPEo5hjZtbGuLNWyjdasLACxq3IEY',
  // escambo.digital proxies /__/auth/ to firebaseapp.com (nginx.conf).
  authDomain: env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'escambo.digital',
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID || 'trocatudo-7f9cc',
  storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'trocatudo-7f9cc.firebasestorage.app',
  messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '34033659666',
  appId: env.REACT_APP_FIREBASE_APP_ID || '1:34033659666:web:4ffaf636005753254bcdcb',
  measurementId: env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-G2JGHJMQ1Q',
};

// When set, every Firebase service talks to the local Emulator Suite on this
// host (staging). Never set in production builds.
export const EMULATOR_HOST = env.REACT_APP_EMULATOR_HOST || null;
// Functions the app calls run in São Paulo and Iowa (docs/D5-region.md); the
// admin picks one in app_settings/functions (Painel Admin > Configurações >
// Região). São Paulo, next to the database, until the setting says otherwise.
export const FUNCTIONS_REGIONS = ['southamerica-east1', 'us-central1'];
let functionsRegion = FUNCTIONS_REGIONS[0];
export const getFunctionsRegion = () => functionsRegion;

// This pattern ensures that Firebase is initialized only once.
 let app;
 if (!getApps().length) {
  app = initializeApp(firebaseConfig);
 } else {
  app = getApps()[0];
 }

//const auth = getAuth(app);
//const db = getFirestore(app);
// --- 2. Initialize Storage ---
// const storage = getStorage(app);
// --- 3. Export storage alongside db and auth ---
// export { db, auth, storage };
// Initialize services from the single 'app' instance and export them
// Initialize Firebase App ONCE
// const app = initializeApp(firebaseConfig);

// App Check (reCAPTCHA Enterprise score key, not secret): proves requests come
// from this site. Firebase only measures it until enforcement is turned on in
// the console. Only on the production domains the key allows: not in
// emulator builds, on localhost (build-time pre-render) or the run.app URL.
const APP_CHECK_SITE_KEY = env.REACT_APP_APPCHECK_SITE_KEY || '6LcunPUtAAAAAJ2fa2xyU7B9EU9pWih5ICbHZuFF';
const APP_CHECK_HOST = /(^|\.)(escambo\.digital|escambodigital\.com\.br)$/;
if (!EMULATOR_HOST && typeof window !== 'undefined' && APP_CHECK_HOST.test(window.location.hostname)
    && !window.__escamboAppCheck) {
  window.__escamboAppCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// Create and export each service from the single app instance
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

if (EMULATOR_HOST && !window.__escamboEmulatorsConnected) {
  window.__escamboEmulatorsConnected = true;
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, EMULATOR_HOST, 8080);
  connectStorageEmulator(storage, EMULATOR_HOST, 9199);
}

const functionsByRegion = {};
const functionsIn = (region) => {
  if (!functionsByRegion[region]) {
    functionsByRegion[region] = getFunctions(app, region);
    if (EMULATOR_HOST) connectFunctionsEmulator(functionsByRegion[region], EMULATOR_HOST, 5001);
  }
  return functionsByRegion[region];
};

/** A callable function; the region is looked up on every call. */
export const callable = (name, options) => (data) =>
  httpsCallable(functionsIn(functionsRegion), name, options)(data);

if (typeof window !== 'undefined') {
  onSnapshot(doc(db, 'app_settings', 'functions'), (snap) => {
    const region = snap.exists() ? snap.data().region : null;
    functionsRegion = FUNCTIONS_REGIONS.includes(region) ? region : FUNCTIONS_REGIONS[0];
  }, () => {});
}

// Messaging needs a secure context + service workers; resolve lazily so
// unsupported browsers (and plain-http staging) don't crash at import time.
export const messagingPromise = isMessagingSupported()
  .then((ok) => (ok && !EMULATOR_HOST ? getMessaging(app) : null))
  .catch(() => null);

/** URL of an HTTP (onRequest) Cloud Function in the current environment. */
export const functionUrl = (name) => (EMULATOR_HOST
  ? `http://${EMULATOR_HOST}:5001/${firebaseConfig.projectId}/${functionsRegion}/${name}`
  : `https://${functionsRegion}-${firebaseConfig.projectId}.cloudfunctions.net/${name}`);
// export { FieldValue };
// --- Re-export Firestore utility functions for centralized access ---
export { 
    FieldValue, 
    serverTimestamp, 
    collection, 
    doc, 
    query, 
    where, 
    onSnapshot,
    getDoc,
    addDoc,
    updateDoc,
    orderBy
};
//
export default app;
